import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from '../services/auth/auth.service';
import { catchError, switchMap } from 'rxjs';
import { RefreshToken } from '../models/refresh-token.model';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ErrorSnackBarComponent } from '../../shared/snack-bars/error-snack-bar/error-snack-bar.component';
import { ErrorResponse } from '../models/error-response.model';
import { SILENCE_ERROR } from '@core/utils/http-context-tokens';
import { RealtimeService } from '@core/services/realtime/realtime.service';

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const _snackBarError = inject(MatSnackBar);
  const authService = inject(AuthService);
  const realtimeService = inject(RealtimeService);
  const token = authService.getToken('accessToken');
  const authReq = req.clone({
    setHeaders: {
      Authorization: `Bearer ${token}`,
    },
  });

  return next(authReq).pipe(
    catchError((err: HttpErrorResponse) => {
      const isSilentRequest = req.context.get(SILENCE_ERROR);
      //Verifica que la ruta que se está consumiendo no sea la de refrescar el token
      // es decir que no se este haciendo petición al endpoint de refrescar tokcen
      // entonces no se valida lo  del error 403 / 401 porque entraría en un bucle
      if (
        authReq.url.includes('v1/auth/staff/refresh') ||
        authReq.url.includes('v1/auth/staff/logout')
      ) {
        authService.clearState();
        if (!isSilentRequest) {
          _snackBarError.openFromComponent(ErrorSnackBarComponent, {
            data: err,
            horizontalPosition: 'end',
            verticalPosition: 'top',
          });
        }
        throw err;
      }
      //Si el error es 403 o 401 significa que la sesión expiro o que el usuario no tiene permisos.
      if (err.status === 403 || err.status === 401) {
        // Verificamos si tenemos un refreshToken
        const refreshToken = authService.getToken('refreshToken');
        if (refreshToken != null) {
          const currentRefreshToken: RefreshToken = {
            refresh_token: refreshToken,
          };
          return authService.refreshToken(currentRefreshToken).pipe(
            switchMap((res) => {
              //Si obtenemos una respuesta para refrescar el token, actualizamos el accessToken.
              authService.setToken('accessToken', res.data);
              realtimeService.updateToken(res.data);
              const newReq = req.clone({
                setHeaders: {
                  Authorization: `Bearer ${res.data}`,
                },
              });
              //Devolvemos la nueva petición con el token actualizado.
              return next(newReq);
            }),
            catchError((err: ErrorResponse) => {
              if (!isSilentRequest) {
                _snackBarError.openFromComponent(ErrorSnackBarComponent, {
                  data: err,
                  horizontalPosition: 'end',
                  verticalPosition: 'top',
                });
              }
              throw err;
            }),
          );
        }
      }

      // Mostramos el mensaje de error al usuario
      if (!isSilentRequest) {
        _snackBarError.openFromComponent(ErrorSnackBarComponent, {
          data: err,
          horizontalPosition: 'end',
          verticalPosition: 'top',
        });
      }
      throw err;
    }),
  );
};
